/**
 * @module spinner
 * @example const formSpinner = new Spinner(document.querySelector('form'));
 * formSpinner.start();
 * formSpinner.stop();
 */

/**
 * Copy shown under the spinner: a string is inserted as text; a Node (e.g. a DocumentFragment built
 * with DOM APIs) is inserted as-is for the rare rich-content case.
 * @typedef {string|Node} SpinnerCopy
 */

export class Spinner {
    /**
     * @returns {string}
     */
    static get SPINNER_TEMPLATE() {
        return `
            <div class="absolute inset-0 w-full h-full bg-black/60" aria-hidden="true"></div>
            <div class="absolute inset-0 w-full h-full flex flex-col items-center justify-center p-16">
                <div class="flex flex-col gap-8 items-center w-full md:max-w-md fg-surface-neutral-default fg-rounded-xl p-24 text-center">
                    <img src="https://public-images.interaction-design.org/ixdf-brand/ixdf-logo-animated.svg" width="80" height="80" alt="" aria-hidden="true">
                    <p role="heading" aria-level="2" class="fg-heading-sm js-spinnerMessage"></p>
                    <p class="fg-body-sm js-spinnerDetails"></p>
                </div>
            </div>
        `;
    }

    /**
     * @param {HTMLElement} target Element to add spinner
     * @param {SpinnerCopy} [message] Message to display below the spinner
     * @param {SpinnerCopy} [details] Details to display below the message
     * @returns {Spinner}
     */
    static createAndStart(target, message = '', details = '') {
        const spinner = new Spinner(target);
        spinner.start(message, details);
        return spinner;
    }

    /**
     * A blocking element with spinner to show the page/element is busy
     * @param {HTMLElement} target Element to add spinner
     * @example
     * const spinner = new Spinner(form);
     * spinner.start('Transaction in progress. Please wait…');
     */
    constructor(target) {
        if (!(target instanceof HTMLElement)) {
            throw new Error('target is not an HTMLElement instance, can not instantiate a Spinner.');
        }
        this._target = target;
        this._containerClasses = ['absolute', 'w-full', 'h-full', 'inset-0', 'js-spinnerContainer'];

        // handle a case when spinner is instantiated before DOM is ready or from another place in the code to deduplicate spinners
        this._spinnerBlock = target.querySelector(':scope > .js-spinnerContainer');
    }

    /**
     * @param {SpinnerCopy} [message] Message to display below the spinner
     * @param {SpinnerCopy} [details] Details to display below the message
     */
    start(message = '', details = '') {
        if (this._spinnerBlock instanceof HTMLElement) {
            this.update(message, details);
            return;
        }

        this._setupTarget();
        this._spinnerBlock = this._createSpinnerContainer(this._target, message, details);
    }

    /** [no description; please add] */
    stop() {
        this._teardownTarget();

        if (!this._spinnerBlock) return;
        this._spinnerBlock.remove();
        this._spinnerBlock = null;
    }

    /**
     * @param {SpinnerCopy} message
     * @param {SpinnerCopy} details
     */
    update(message, details) {
        this._spinnerBlock.innerHTML = Spinner.SPINNER_TEMPLATE;
        this._spinnerBlock.querySelector('.js-spinnerMessage').replaceChildren(message);
        this._spinnerBlock.querySelector('.js-spinnerDetails').replaceChildren(details);
    }

    /**
     * @param {string} className HTML class name to add
     */
    _addClass(className) {
        this._containerClasses.push(className);
    }

    /**
     * @param {HTMLElement} target Element to create container
     * @param {SpinnerCopy} message Message to display
     * @param {SpinnerCopy} details Details to display
     * @returns {HTMLElement}
     */
    _createSpinnerContainer(target, message, details) {
        const spinnerContainer = document.createElement('div');
        spinnerContainer.classList.add(...this._containerClasses);
        spinnerContainer.setAttribute('role', 'status');
        spinnerContainer.insertAdjacentHTML('afterbegin', Spinner.SPINNER_TEMPLATE);
        spinnerContainer.querySelector('.js-spinnerMessage').replaceChildren(message);
        spinnerContainer.querySelector('.js-spinnerDetails').replaceChildren(details);

        target.append(spinnerContainer);

        return spinnerContainer;
    }

    /** [no description; please add] */
    _setupTarget() {
        this._target.classList.add('relative');
        this._target.setAttribute('aria-busy', 'true');
    }

    /** [no description; please add] */
    _teardownTarget() {
        this._target.classList.remove('relative');
        this._target.setAttribute('aria-busy', 'false');
    }
}

export const PageSpinner = (function () {
    let instance;

    /**
     * @returns {Spinner} Page spinner instance
     */
    function getInstance() {
        if (!instance) {
            instance = new Spinner(document.body);
            instance._addClass('fixed');
            instance._addClass('z-(--z-dialog)');
        }
        return instance;
    }

    return {
        /**
         @param {SpinnerCopy} [message] Message to display
         @param {SpinnerCopy} [details] Details to display
         */
        start: (message, details) => {
            getInstance().start(message, details);
        },
        /** [no description; please add] */
        stop: () => {
            getInstance().stop();
        },
    };
})();
